import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2, Check, X, ShoppingBag, ShieldCheck, Zap, Search } from "lucide-react";
import { checkDomain } from "../../lib/api";

const EASE = [0.16, 1, 0.3, 1];
const TLDS = [".sa", ".com.sa", ".org.sa", ".net.sa", ".gov.sa", ".edu.sa"];

export const HeroA = ({ onAddToCart }) => {
    const { t } = useTranslation();
    const [query, setQuery] = useState("");
    const [activeTLD, setActiveTLD] = useState(".sa");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    const submit = async (e) => {
        e?.preventDefault?.();
        const q = query.trim();
        if (!q) return;
        setLoading(true); setError(""); setResult(null);
        try {
            const data = await checkDomain(q);
            setResult(data);
        } catch (err) {
            setError(
                err?.response?.data?.detail === "invalid_domain_label"
                    ? "Try a clean label, e.g. mybrand"
                    : "Could not check availability — try again.",
            );
        } finally { setLoading(false); }
    };

    return (
        <section
            id="hero-search"
            className="relative isolate overflow-hidden bg-limestone-100 pt-[110px] lg:pt-[140px]"
            data-testid="hero-a"
        >
            {/* Architectural Asir stripe — top decoration */}
            <div className="asir-stripe absolute inset-x-0 top-0 h-[6px]" aria-hidden="true" />

            <div className="relative mx-auto max-w-[1320px] px-5 pb-20 lg:px-8 lg:pb-28">
                <div className="grid grid-cols-12 gap-6 lg:gap-10">
                    {/* LEFT: Content column (8/12) */}
                    <div className="col-span-12 lg:col-span-8">
                        {/* Trust chip */}
                        <motion.div
                            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45, ease: EASE }}
                            className="inline-flex items-center gap-2 rounded-[8px] border-2 border-ink-800 bg-white px-3.5 py-2"
                            data-testid="hero-kicker"
                        >
                            <ShieldCheck className="h-4 w-4 text-madder-500" />
                            <span className="text-[14px] font-bold uppercase tracking-[0.12em] text-ink-800">
                                {t("home.heroKicker")}
                            </span>
                        </motion.div>

                        {/* Headline */}
                        <motion.h1
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: EASE, delay: 0.05 }}
                            className="display-headline mt-7 max-w-[16ch]"
                            data-testid="hero-title"
                        >
                            {t("home.heroQuestion1")}{" "}
                            <span style={{ color: "#8A1E12" }}>{t("home.heroQuestionAccent")}</span>{" "}
                            {t("home.heroQuestion2")}
                        </motion.h1>

                        {/* Lead */}
                        <motion.p
                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease: EASE, delay: 0.12 }}
                            className="lead mt-7 max-w-[58ch]"
                        >
                            {t("home.heroSubtitle")}
                        </motion.p>

                        {/* Search shell — 76px tall, 2px ink border, embedded madder button */}
                        <motion.form
                            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease: EASE, delay: 0.2 }}
                            onSubmit={submit}
                            className="mt-9 max-w-[780px]"
                            data-testid="hero-search-form"
                        >
                            <div className="search-shell flex items-center gap-2 px-3">
                                <span className="ps-3 text-ink-600">
                                    <Search className="h-6 w-6" strokeWidth={2.4} />
                                </span>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={t("home.heroSearchPlaceholder")}
                                    aria-label={t("home.heroSearchPlaceholder")}
                                    className="h-full flex-1 bg-transparent px-2 text-[20px] font-medium text-ink-800 placeholder:text-ink-400 focus:outline-none"
                                    data-testid="hero-search-input"
                                />
                                <button
                                    type="submit"
                                    disabled={loading || !query.trim()}
                                    data-testid="hero-search-submit"
                                    className="inline-flex h-[60px] min-w-[160px] items-center justify-center gap-2 rounded-[10px] bg-madder-500 px-6 text-[17px] font-bold text-white shadow-madder transition-all duration-200 hover:bg-madder-600 active:translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {loading ? (
                                        <><Loader2 className="h-5 w-5 animate-spin" />{t("cta.checking")}</>
                                    ) : (
                                        <>{t("cta.search")}<ArrowRight className="h-5 w-5 rtl-mirror" /></>
                                    )}
                                </button>
                            </div>

                            {/* TLD chips — large pillows with bold borders */}
                            <div className="mt-5 flex flex-wrap gap-2">
                                {TLDS.map((tld) => {
                                    const active = activeTLD === tld;
                                    return (
                                        <button
                                            type="button"
                                            key={tld}
                                            onClick={() => {
                                                setActiveTLD(tld);
                                                const base = (query.trim() || "").replace(/\.(sa|com\.sa|org\.sa|net\.sa|gov\.sa|edu\.sa|com|net)$/i, "");
                                                setQuery((base || "mybrand") + tld);
                                                setTimeout(() => inputRef.current?.focus(), 0);
                                            }}
                                            data-testid={`hero-tld-chip-${tld.replace(/\./g, "")}`}
                                            className={`inline-flex h-12 min-h-[48px] items-center rounded-[8px] border-2 px-4 font-mono text-[15px] font-semibold transition-all duration-200 ease-nour num-ltr ${
                                                active
                                                    ? "border-ink-800 bg-ink-800 text-white"
                                                    : "border-ink-300 bg-white text-ink-800 hover:border-ink-800"
                                            }`}
                                        >
                                            {tld}
                                        </button>
                                    );
                                })}
                            </div>

                            <p className="mt-5 text-[16px] font-medium text-ink-700">
                                {t("home.heroSearchHint")} <span className="text-ink-400">·</span>{" "}
                                <span className="num-ltr tabular-nums">{t("home.heroFootnote")}</span>
                            </p>
                        </motion.form>

                        {/* Result panel */}
                        <AnimatePresence mode="wait">
                            {(loading || result || error) && (
                                <motion.div
                                    key={loading ? "load" : result ? "ok" : "err"}
                                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.24, ease: EASE }}
                                    className="mt-7 max-w-[780px]"
                                    data-testid="hero-result-panel"
                                >
                                    {loading && (
                                        <div className="rounded-card border-2 border-ink-200 bg-white p-6">
                                            <div className="h-4 w-44 animate-pulse rounded-full bg-ink-100" />
                                            <div className="mt-4 h-4 w-full animate-pulse rounded-full bg-ink-100" />
                                        </div>
                                    )}
                                    {error && !loading && (
                                        <div className="rounded-card border-2 border-madder-500 bg-madder-50 p-5 text-[16px] font-semibold text-madder-700">{error}</div>
                                    )}
                                    {result && !loading && (
                                        <div className="rounded-card border-2 border-ink-800 bg-white p-7">
                                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                                <div className="flex items-center gap-4">
                                                    <span className={`inline-flex h-14 w-14 items-center justify-center rounded-[10px] ${result.primary.available ? "bg-redsea-500 text-white" : "bg-ink-100 text-ink-500"}`}>
                                                        {result.primary.available ? <Check className="h-7 w-7" strokeWidth={3} /> : <X className="h-7 w-7" strokeWidth={3} />}
                                                    </span>
                                                    <div>
                                                        <p className="font-mono text-[20px] font-bold text-ink-800 num-ltr">{result.primary.domain}</p>
                                                        <p className="text-[16px] font-semibold mt-1">
                                                            {result.primary.available ? <span className="text-redsea-500">{t("domainResult.available")}</span> : <span className="text-ink-500">{t("domainResult.taken")}</span>}
                                                            {result.primary.available && (
                                                                <> <span className="text-ink-400">·</span> <span className="num-ltr tabular-nums text-ink-800">{result.primary.price_sar} {t("domainResult.perYear")}</span></>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                                {result.primary.available && (
                                                    <button
                                                        type="button"
                                                        onClick={() => onAddToCart?.(result.primary)}
                                                        data-testid="hero-add-to-cart"
                                                        className="inline-flex h-[56px] items-center gap-2 rounded-btn bg-souq-400 px-6 text-[16px] font-bold text-ink-800 transition-all duration-200 hover:bg-souq-500 hover:text-white"
                                                    >
                                                        <ShoppingBag className="h-5 w-5" />
                                                        {t("domainResult.addToCart")}
                                                    </button>
                                                )}
                                            </div>
                                            <ul className="mt-5 grid grid-cols-1 gap-1 border-t-2 border-ink-100 pt-4 sm:grid-cols-2">
                                                {result.suggestions.map((s, i) => (
                                                    <motion.li
                                                        key={s.domain}
                                                        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.22, ease: EASE, delay: i * 0.04 }}
                                                        className="flex items-center justify-between rounded-[8px] px-3 py-3 transition-colors duration-200 hover:bg-mudbrick-200"
                                                    >
                                                        <span className="font-mono text-[16px] font-semibold text-ink-800 num-ltr">{s.domain}</span>
                                                        <div className="flex items-center gap-3">
                                                            {s.available ? (
                                                                <>
                                                                    <span className="inline-flex items-center gap-1 text-[14px] font-bold text-redsea-500 num-ltr tabular-nums">{s.price_sar} SAR</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => onAddToCart?.(s)}
                                                                        data-testid={`hero-suggestion-add-${s.domain}`}
                                                                        aria-label={`Add ${s.domain} to cart`}
                                                                        className="inline-flex h-9 items-center gap-1.5 rounded-[6px] bg-madder-500 px-3 text-[12px] font-bold text-white transition-colors duration-200 hover:bg-madder-600"
                                                                    >
                                                                        <ShoppingBag className="h-3.5 w-3.5" />
                                                                        {t("domainResult.addToCart")}
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <span className="text-[13px] font-medium text-ink-400">{t("domainResult.taken")}</span>
                                                            )}
                                                        </div>
                                                    </motion.li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* RIGHT: Architectural texture column (4/12) — visible on lg+ */}
                    <motion.aside
                        initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, ease: EASE, delay: 0.25 }}
                        className="hidden lg:flex lg:col-span-4 flex-col gap-4"
                        aria-hidden="true"
                    >
                        {/* SaudiNIC pillar — madder block */}
                        <div className="bg-madder-grain rounded-card p-7 text-white shadow-madder">
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="h-5 w-5" />
                                <p className="text-[12px] font-bold uppercase tracking-[0.18em]">SaudiNIC</p>
                            </div>
                            <p className="mt-5 font-display text-[28px] font-bold leading-tight">
                                Accredited<br />.sa registrar
                            </p>
                            <div className="mt-5 inline-flex h-9 items-center rounded-[6px] bg-white/15 px-3 font-mono text-[12px] font-bold tracking-wider">
                                ZATCA · PDPL
                            </div>
                        </div>

                        {/* Speed pillar — souq yellow block */}
                        <div className="rounded-card bg-souq-400 p-7 text-ink-900">
                            <div className="flex items-center gap-2">
                                <Zap className="h-5 w-5" strokeWidth={2.6} />
                                <p className="text-[12px] font-bold uppercase tracking-[0.18em]">Speed</p>
                            </div>
                            <p className="mt-5 font-display text-[44px] font-extrabold leading-none tabular-nums num-ltr">
                                &lt; 60s
                            </p>
                            <p className="mt-2 text-[15px] font-bold">to register a Saudi domain.</p>
                        </div>

                        {/* Trust pillar — teal block */}
                        <div className="bg-redsea-grain rounded-card p-7 text-white shadow-redsea">
                            <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-souq-200">Trusted</p>
                            <p className="mt-5 font-display text-[40px] font-extrabold leading-none tabular-nums num-ltr">
                                240,000+
                            </p>
                            <p className="mt-2 text-[15px] font-semibold text-redsea-100">Saudi domains under management.</p>
                        </div>
                    </motion.aside>
                </div>
            </div>
        </section>
    );
};
